/**
 * Page context — the Pages strip.
 *
 * Publishes a PoseAdapter for the shared PoseStrip (via the strip-tabs
 * coordinator) when the selection is inside a Pages container: one card per
 * page (click = navigate, double-click = rename, context menu =
 * duplicate/delete, + = add page), an "All" card, and a full breadcrumb for
 * the container → page → … → selection trail.
 *
 * "SHARED" IS A VIRTUAL PARENT CONTEXT (the Shared intent, `setSharedIntent`): the separated
 * first card. Selecting it declares "I'm authoring shared content" — no
 * visibility change (the panel's show-all overview owns that). Content
 * created there lands FLAT inside the lazily-created "Shared" group (a real
 * excluded-from-paging child, `ensureSharedGroup`); edits are base edits. The
 * intent clears when navigation or selection moves to a page.
 *
 * SHARED objects appear as virtual entries in the breadcrumb's drilldown lists
 * (a page's top-level objects include them — they're visible on every page)
 * with a THUMBTACK to pin/unpin: pin moves a page child out to the container
 * and registers it shared; unpin moves it back into the current page. Both are
 * single undoable gestures (`trace-viewer/editor`'s shared-children actions).
 *
 * While the pages tab is open (page-editing-mode.ts), edits to SHARED children
 * route into the showing page's state (trace-viewer's capturePageState).
 * Selecting an object on a non-active page still auto-navigates (instant) —
 * except in the All overview, where everything is already visible.
 */

import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import type { UndoCommand } from 'trace-viewer/editor';
import { Properties } from 'trace-viewer/behaviors';
import type { CrumbEntry, CrumbEntryPin } from 'trace-viewer/editor';
import type { PoseAdapter, PoseCrumb } from './pose-strip';
import { getStripTabs } from './strip-tabs';
import { setPageEditingEnabled } from './page-editing-mode';
import {
    addPage, deletePage, renamePage, duplicatePage, reorderPages, pageChildrenOf, resolvePageContext,
    isSharedIntent, setSharedIntent, shareIntoGroup, unshare, unshareFromGroup, findSharedGroup,
} from 'trace-viewer/editor';
import type { PageContext } from 'trace-viewer/editor';
import { pushCommand, pushOp } from './behaviors/pagesSharedActions';
import { makePageThumbnailProvider, sharedChildrenOf } from './page-thumbnails';

/** True when `node` is `root` itself or anywhere below it. */
function within(node: SceneObject, root: SceneObject): boolean {
    let cur: SceneObject | null = node;
    while (cur) {
        if (cur === root) return true;
        cur = cur.getParent();
    }
    return false;
}

export interface PageContextControls {
    /** Update the strip based on current selection. Pass null to hide. */
    update: (selectedObject: SceneObject | null) => void;
    /** Clean up */
    dispose: () => void;
}

/** Push a pin gesture's command and redraw; a failure is logged, never thrown. */
async function runPin(action: Promise<UndoCommand | null>, rerender: () => void): Promise<void> {
    try {
        pushCommand(await action);
    } catch (error) {
        console.error('[Pages] pin failed:', error);
    }
    rerender();
}

/**
 * Build the strip breadcrumb: the FULL hierarchy path container → … →
 * selection (like the states breadcrumb), so top-level page content is
 * drill-down-reachable. Shared objects appear as VIRTUAL entries in the
 * page-level lists (they're visible on every page) and every top-level
 * content entry carries a thumbtack pin (see module docs).
 */
function buildPagesBreadcrumb(
    ctx: PageContext,
    selected: SceneObject,
    traceScene: TraceScene,
    rerender: () => void,
): PoseCrumb[] {
    const { container } = ctx;
    const pageChildren = pageChildrenOf(container);
    const sharedChildren = sharedChildrenOf(container);
    const group = findSharedGroup(container);
    const isPage = (o: SceneObject) => pageChildren.includes(o);
    const isSharedRoot = (o: SceneObject) => sharedChildren.includes(o);

    const pinFor = (obj: SceneObject, shared: boolean): CrumbEntryPin => ({
        on: shared,
        title: shared
            ? `"${obj.name}" is shared across pages — click to move it into the current page`
            : `Share "${obj.name}" across all pages (moves it into the Shared group)`,
        onToggle: () => {
            if (shared) {
                const dest = pageChildren[ctx.activeIndex] ?? pageChildren[0];
                if (!dest) return;
                const action = obj.getParent() === group
                    ? unshareFromGroup(container, obj, dest)
                    : unshare(container, obj, dest);
                void runPin(action, rerender);
            } else {
                void runPin(shareIntoGroup(container, obj), rerender);
            }
        },
    });
    /** Pin for an arbitrary entry: shared objects unpin (group children +
     *  flat shared roots — but never the group node itself, unpinning the
     *  whole group into a page would be a surprise); page children pin. */
    const pinOf = (obj: SceneObject): CrumbEntryPin | undefined => {
        if (obj === group) return undefined;
        if (isSharedRoot(obj) || obj.getParent() === group) return pinFor(obj, true);
        const parent = obj.getParent();
        if (parent && isPage(parent)) return pinFor(obj, false);
        return undefined;
    };
    const entry = (o: SceneObject): CrumbEntry => ({ object: o, label: o.name, pin: pinOf(o) });
    /** The shared OBJECTS for page-level lists: the group's contents inlined
     *  (picking one selects that object, not the group) plus flat shared
     *  roots. */
    const sharedEntries = (): CrumbEntry[] => sharedChildren.flatMap(s =>
        (s === group ? s.getChildren() : [s]).map(entry));

    // Path: selection → up to the container (inclusive).
    const path: SceneObject[] = [];
    let cur: SceneObject | null = selected;
    while (cur) {
        path.unshift(cur);
        if (cur === container) break;
        cur = cur.getParent();
    }
    if (path[0] !== container) path.unshift(container);

    return path.map((o) => {
        const isLeaf = o === selected;
        const topLevel = o.getParent() === container;

        let siblings: CrumbEntry[] = [];
        if (o !== container) {
            if (topLevel) {
                // Lateral hops at the top level: the pages plus the (virtual)
                // shared objects, minus the crumb itself.
                siblings = [
                    ...pageChildren.filter(p => p !== o).map(entry),
                    ...sharedEntries().filter(e => e.object !== o),
                ];
            } else {
                siblings = (o.getParent()?.getChildren() ?? []).filter(s => s !== o).map(entry);
                // Peers ON A PAGE include the virtual shared objects.
                if (o.getParent() && isPage(o.getParent()!)) siblings.push(...sharedEntries());
            }
        }

        let children: CrumbEntry[] = [];
        if (isLeaf) {
            if (o === container) {
                children = [...pageChildren.map(entry), ...sharedEntries()];
            } else if (isPage(o)) {
                // Drilldown into a page's top-level objects — real children
                // (pinnable) plus the virtual shared objects (unpinnable).
                children = [...o.getChildren().map(entry), ...sharedEntries()];
            } else {
                children = o.getChildren().map(entry);
            }
        }

        return {
            object: o,
            label: o.name,
            isContext: o === container,
            isSelection: isLeaf,
            siblings,
            children,
        };
    });
}

function makePagesAdapter(
    traceScene: TraceScene,
    ctx: PageContext,
    selected: SceneObject,
    rerender: () => void,
): PoseAdapter {
    const { container, pages } = ctx;
    const names = (): string[] => pageChildrenOf(container).map(p => p.name);
    const indexOf = (name: string): number => names().indexOf(name);

    // Selection-tab thumbtack: shared objects (group children + flat shared
    // roots, never the group itself) → pinned (unpin = move into the current
    // page); top-level page child → unpinned (pin = move into the Shared
    // group). Pages and deeper objects carry no pin.
    const sharedChildren = sharedChildrenOf(container);
    const group = findSharedGroup(container);
    const selParent = selected.getParent();
    const selectedIsShared = selected !== group
        && (sharedChildren.includes(selected) || (!!group && selParent === group));
    const selectedIsPageChild = !!selParent
        && pageChildrenOf(container).includes(selParent);
    const pin = (selectedIsShared || selectedIsPageChild) ? {
        on: selectedIsShared,
        onToggle: (next: boolean) => {
            if (next) {
                void runPin(shareIntoGroup(container, selected), rerender);
            } else {
                const pageChildren = pageChildrenOf(container);
                const dest = pageChildren[ctx.activeIndex] ?? pageChildren[0];
                if (!dest) return;
                const action = selParent === group
                    ? unshareFromGroup(container, selected, dest)
                    : unshare(container, selected, dest);
                void runPin(action, rerender);
            }
        },
    } : null;

    // Card/nav selection — see the `select` doc below. Shared card = null.
    const stripSelect = (name: string | null, animate: boolean): void => {
        if (name === null) {
            setSharedIntent(container, true);
            for (const n of pages.getPageNodes()) n.setEnabled(false);
            for (const s of sharedChildrenOf(container)) {
                for (const o of [s, ...s.getDescendants()]) {
                    const props = o.getBehavior(Properties);
                    if (props && props.getActiveState() !== undefined) props.setState(undefined, true);
                }
            }
            const group = findSharedGroup(container);
            traceScene.select(group ?? container);
            return;
        }
        setSharedIntent(container, false);
        const idx = indexOf(name);
        if (idx < 0) return;
        pages.showPage(idx, animate);
        const newPage = pageChildrenOf(container)[idx];
        // Keep shared CONTENT selected across switches (the per-page
        // positioning workflow) — but never the Shared GROUP node itself:
        // it's a folder, not a positioning target, and leaving it selected
        // makes it the Delete target and the creation parent while a page
        // card reads as active.
        const keepShared = selected !== group
            && sharedChildrenOf(container).some(s => within(selected, s));
        if (newPage && !keepShared) traceScene.select(newPage);
    };

    return {
        owner: container,
        ownerName: container.name,
        noun: 'page',
        baseLabel: 'Shared',
        baseTitle: 'Shared — content that lives on every page. Select to author shared content: new objects land in the Shared group, edits change the base scene. (Show-all overview lives in the behavior panel.)',
        baseBadge: null,
        baseSeparated: true,
        corner: 'static',

        getNames: names,
        // The Shared card reflects the editor INTENT (`isSharedIntent`).
        // Shared mode shows shared content ALONE (every page hidden), so if
        // something external shows a page (AutoUI next/prev, behaviors), that
        // wins — reconcile by dropping the intent.
        getActive: () => {
            if (isSharedIntent(container)) {
                if (pages.getPageNodes().some(n => n.isEnabled(false))) {
                    setSharedIntent(container, false);
                } else {
                    return null;
                }
            }
            const idx = pages.getActivePageIndex();
            return names()[idx] ?? null;
        },
        // ONE selection, ONE context: a page card SELECTS that page (creation
        // parenting and the breadcrumb simply follow the selection) — except
        // a SHARED selection is kept: shared content stays visible across
        // switches, and flipping pages with a shared object selected is the
        // per-page positioning workflow. The Shared card shows shared content
        // alone at its BASE look (pages hidden via direct setEnabled — no
        // authored write; the next showPage restores them — and any page-
        // applied states on shared subtrees reverted, so edits there are
        // plain base edits, not fights with a lingering state override).
        select: stripSelect,
        add: () => { void addPage(container).then(pushOp).then(rerender); },
        remove: (name) => {
            const idx = indexOf(name);
            if (idx >= 0) void deletePage(container, idx).then(pushOp).then(rerender);
        },
        rename: (oldName, newName) => {
            const idx = indexOf(oldName);
            if (idx >= 0) void renamePage(container, idx, newName).then(pushCommand).then(rerender);
        },
        duplicate: (name) => {
            const idx = indexOf(name);
            if (idx >= 0) void duplicatePage(container, idx).then(pushOp).then(rerender);
        },
        reorder: (from, slot) => {
            void reorderPages(container, from, slot).then(pushCommand).then(rerender);
        },

        // Page cards preview each page's look (the same posed-capture system
        // the states strip uses; see page-thumbnails.ts).
        thumbnails: makePageThumbnailProvider(container, pages),

        // Opening/closing the pages tab drives page-editing mode (the capture
        // gate). The strip-tabs coordinator calls this on open/swap/close.
        onToggleEnabled: (enabled) => {
            setPageEditingEnabled(enabled);
            rerender();
        },
        // Only the open tab's strip renders, so the frame never conflicts with
        // the states frame. The frame header shows the ACTIVE page name with
        // prev/next flanking it (wrap-around; same path as the page cards, so
        // shared selections are kept and the shared intent clears).
        frameLabel: container.name,
        frameNav: {
            onPrev: () => {
                const list = names();
                if (list.length < 2 && !isSharedIntent(container)) return;
                const cur = pages.getActivePageIndex();
                stripSelect(list[(cur - 1 + list.length) % list.length] ?? null, true);
            },
            onNext: () => {
                const list = names();
                if (list.length < 2 && !isSharedIntent(container)) return;
                const cur = pages.getActivePageIndex();
                stripSelect(list[(cur + 1) % list.length] ?? null, true);
            },
        },
        breadcrumb: buildPagesBreadcrumb(ctx, selected, traceScene, rerender),
        onSelectObject: (o) => o.traceScene.select(o),
        pin,
    };
}

export function mountPageContext(): PageContextControls {
    // Render sink: the shared strip-tabs host (one bottom-left slot for Pages +
    // States). Publishing null withdraws the pages tab.
    const tabs = getStripTabs();

    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let lastSelected: SceneObject | null = null;
    /** True only on the first doUpdate after a selection change — prevents auto-navigate from fighting other page switches (e.g. AutoUI next/prev). */
    let isInitialSelect = false;

    function doUpdate(selected: SceneObject | null) {
        if (!selected) {
            tabs.publish('pages', null);
            return;
        }

        const ctx = resolvePageContext(selected);
        const traceScene = selected.traceScene;
        if (!ctx || !traceScene) {
            tabs.publish('pages', null);
            return;
        }

        // Auto-navigate: only on initial selection, not on poll ticks.
        // This prevents fighting with other page-switching mechanisms (AutoUI next/prev, behavior showPage, etc.).
        // Deliberately INSTANT (animate: false): this is an editing-context jump —
        // the user selected an object to work on it, and a transition playing
        // underneath a gizmo interaction would fight the edit. User-facing page
        // navigation (strip cards, panel prev/next) animates. UNCONDITIONAL
        // (not gated on the pages tab): it's navigation, not capture — selecting
        // an object on a hidden page must reveal it regardless of edit mode.
        // Skipped during the panel's show-all overview (everything is visible).
        // A container with no pages has only the Shared context — the strip
        // shows the Shared card active, so keep the intent flag in sync
        // (creation routing and `pageEditing` read it). Adding the
        // first page selects it, which clears the intent below.
        if (ctx.pageCount === 0 && !isSharedIntent(ctx.container)) {
            setSharedIntent(ctx.container, true);
        }

        if (isInitialSelect && ctx.selectedPage) {
            // Selecting page content leaves the Shared context — the context
            // follows the selection. Leaving shared mode must re-show the
            // page (shared mode hid every page node).
            const wasShared = isSharedIntent(ctx.container);
            if (wasShared) setSharedIntent(ctx.container, false);
            if (ctx.selectedPageIndex >= 0
                && (wasShared
                    || (ctx.selectedPageIndex !== ctx.activeIndex && !ctx.pages.isShowingAll()))) {
                ctx.pages.showPage(ctx.selectedPageIndex, false);
            }
        }
        isInitialSelect = false;

        tabs.publish('pages', makePagesAdapter(traceScene, ctx, selected, () => doUpdate(lastSelected)));
    }

    function update(selected: SceneObject | null) {
        lastSelected = selected;
        isInitialSelect = true;
        doUpdate(selected);

        // Poll so the adapter (breadcrumb, availability) tracks changes the
        // strip's own name/active poll can't see.
        if (pollInterval) clearInterval(pollInterval);
        if (selected) {
            pollInterval = setInterval(() => doUpdate(lastSelected), 500);
        }
    }

    return {
        update,
        dispose: () => {
            if (pollInterval) clearInterval(pollInterval);
            pollInterval = null;
            tabs.publish('pages', null);
        },
    };
}
